import { t } from "@lingui/macro"
import { FormHelperText, IconButton, Link, Stack } from "@mui/material"
import { observer } from "mobx-react"
import { useCallback, useMemo } from "react"

import { UploadDocumentField } from "src/components/AttachmentsField/DocumentAttachments/UploadDocumentField"
import { Bin24 } from "src/components/icons/Bin24"
import { Pdf24 } from "src/components/icons/Pdf24"
import { formatFileSize } from "src/lib/number"
import { FileStyledStack } from "src/modals/knowledge-items/formFields/styled"
import { KnowledgeItemStore } from "src/modals/knowledge-items/store"
import { useStore } from "src/store/lib/useStore"

export const KnowledgeItemsFile = observer(() => {
    const store = useStore(KnowledgeItemStore)
    const file = store.formFields.get("content")
    const accessType = store.formFields.get("access_type")
    const error = store.formFields.error("content")

    const handleFileChange = useCallback(
        (files) => {
            store.formFields.set("content", files[0])
        },
        [store.formFields],
    )

    const handleError = useCallback(
        (errorMessage: string) => {
            store.formFields.setError("content", errorMessage)
        },
        [store.formFields],
    )

    const handleRemoveFile = useCallback(() => {
        store.formFields.set("content", undefined)
    }, [store.formFields])

    const isDisabled = useMemo(() => accessType === "READ", [accessType])
    const hasError = useMemo(() => Boolean(error), [error])
    const fileName = useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!file) return ""
        return typeof file === "string"
            ? `${t`knowledge-item-modal.uploaded-file-name`}.${file
                  .split(".")
                  .pop()}`
            : file.file.name
    }, [file])
    const fileSize = useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!file || typeof file === "string") return ""
        return formatFileSize(file.file.size)
    }, [file])

    return (
        <Stack spacing={2}>
            <div>
                <UploadDocumentField
                    onChange={handleFileChange}
                    disabled={isDisabled}
                    max={1}
                    placeholder={t`knowledge-item-modal.file-placeholder`}
                    onError={handleError}
                />

                <FormHelperText
                    sx={{
                        margin: "3px 14px 0",
                    }}
                    error={hasError}
                >
                    {error ?? t`knowledge-item-modal.file-helper`}
                </FormHelperText>
            </div>

            {file != null && (
                <FileStyledStack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Stack
                        component="span"
                        spacing={1}
                        direction="row"
                        alignItems="center"
                    >
                        <Pdf24 style={{ flexShrink: "0" }} />
                        <Link
                            href={typeof file === "string" ? file : file.url}
                            target="_blank"
                        >
                            {fileName}
                        </Link>
                    </Stack>
                    <Stack
                        component="span"
                        spacing={1}
                        direction="row"
                        alignItems="center"
                    >
                        <span style={{ whiteSpace: "nowrap" }}>{fileSize}</span>
                        <IconButton
                            onClick={handleRemoveFile}
                            title={t`knowledge-item-modal.remove-file`}
                            disabled={isDisabled}
                        >
                            <Bin24 />
                        </IconButton>
                    </Stack>
                </FileStyledStack>
            )}
        </Stack>
    )
})
